import React, { useEffect, useState } from 'react';
import {
    Select, MenuItem, Stack, IconButton, Pagination,
    TextField, Box, InputLabel, FormControl, Typography
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useDebounceEffect } from '../inputs/cropImage/useDebounceEffect';
import { ICustomPaginationProps } from '~/shared/types/types';

export const CustomPagination: React.FC<ICustomPaginationProps> = ({
    page,
    pageSize,
    rowCount,
    search,
    onSearchChange,
    onPageChange,
    onPageSizeChange,
}) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / pageSize));

    const [inputPage, setInputPage] = useState(page + 1);
    const [localPageSize, setLocalPageSize] = useState(pageSize);
    const [localSearchValue, setLocalSearchValue] = useState(search || '');

    useEffect(() => setInputPage(page + 1), [page]);
    useEffect(() => setLocalPageSize(pageSize), [pageSize]);
    useEffect(() => setLocalSearchValue(search as any), [search]);

    useDebounceEffect(() => {
        if (localSearchValue !== search) {
            onPageChange(0);
            onSearchChange(localSearchValue.trim());
        }
    }, 500, [localSearchValue]);

    useDebounceEffect(() => {
        if (localPageSize !== pageSize) {
            onPageSizeChange(localPageSize);
        }
    }, 1000, [localPageSize]);

    useDebounceEffect(() => {
        if (inputPage >= 1 && inputPage <= totalPages && inputPage !== page + 1) {
            onPageChange(inputPage - 1);
        }
    }, 1000, [inputPage]);


    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            height={60}
            spacing={1}
            sx={{
                backgroundColor: 'var(--template-palette-background-paper)',
                p: 2.5,
                borderRadius: 1,
            }}
        >
            {/* Existing pagination stack */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'flex-end' }}>
                <IconButton
                    onClick={() => onPageChange(0)}
                    disabled={page === 0}
                    sx={{
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 1,
                        padding: 0.5,
                        height: 35,
                    }}
                >
                    <FirstPageIcon fontSize="small" />
                </IconButton>

                <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(_, value) => onPageChange(value - 1)}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                        height: '100%',
                        '&.MuiPagination-root': {
                            height: 35,
                            display: 'flex',
                            alignItems: 'stretch',
                        },
                        '& .MuiPagination-ul': {
                            height: 35,
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: '4px',
                        },
                        '& .MuiPaginationItem-root': {
                            height: 35,
                            width: 35,
                            padding: 0,
                            borderRadius: 1,
                            border: '1px solid rgba(255,255,255,0.12)',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                        '& .Mui-selected': {
                            height: 35,
                            borderColor: '#555',
                            outline: 'none',
                        },
                    }}
                />

                <IconButton
                    onClick={() => onPageChange(totalPages - 1)}
                    disabled={page >= totalPages - 1}
                    sx={{
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 1,
                        padding: 0.5,
                        height: 35,
                    }}
                >
                    <LastPageIcon fontSize="small" />
                </IconButton>

                <Box ml={2} display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">Записей:</Typography>
                    <FormControl size="small">
                        <Select
                            size="small"
                            value={pageSize}
                            onChange={(e) => setLocalPageSize(Number(e.target.value))}
                            sx={{
                                height: 35,
                                minWidth: 70,
                                borderRadius: 1,
                                '& .MuiSelect-select': {
                                    padding: '4px 24px 4px 8px',
                                },
                            }}
                        >
                            {[10, 25, 50, 100].map(size => (
                                <MenuItem key={size} value={size}>{size}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box ml={2} display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">Стр.:</Typography>
                    <TextField
                        type="number"
                        size="small"
                        value={inputPage}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (!isNaN(value)) setInputPage(value);
                        }}
                        sx={{
                            width: 60,
                            '& input': { textAlign: 'center', padding: '4px' },
                            '& .MuiOutlinedInput-root': {
                                height: 35,
                                borderRadius: 1,
                                fontSize: 14,
                            },
                        }}
                        slotProps={{
                            input: {
                                inputProps: {
                                    min: 1,
                                    max: totalPages,
                                },
                            },
                        }}
                    />
                </Box>
            </Stack>
        </Stack>
    );
};
